import type { ReactNode } from 'react'
import { TrustGauge } from '@carouselgg/nexus/trust-gauge'
import { DisplayHeading } from '@carouselgg/nexus/display-heading'
import { TRUST_LEVELS, nextTrustLevel } from '@carouselgg/auth/trust-levels'
import type { TrustLevel } from '@carouselgg/auth/types'
import styles from './TrustHero.module.css'

export interface TrustHeroProps {
  score: number
  level: TrustLevel
  /** "Your Carousel score", "Your Counter-Strike 2 score". */
  eyebrow: string
  /** What this number is and who checks it. */
  children?: ReactNode
}

/**
 * The score in its ring, the level it earns, how far the next one is, and the
 * whole ladder of levels underneath so the number has somewhere to sit.
 */
export function TrustHero({ score, level, eyebrow, children }: TrustHeroProps) {
  const next = nextTrustLevel(score)

  return (
    <section className={styles.hero} aria-labelledby="trust-hero-level">
      <div className={styles.top}>
        <div className={styles.headline}>
          <TrustGauge score={score} size={112} title={`${eyebrow}: ${score} out of 100`} />
          <div className={styles.titles}>
            <span className={styles.eyebrow}>{eyebrow}</span>
            <DisplayHeading as="h1" size="5xl" id="trust-hero-level" className={styles.level}>{level}</DisplayHeading>
            <span className={styles.next}>
              {next
                ? <>{next.pointsToGo} {next.pointsToGo === 1 ? 'point' : 'points'} to <b>{next.level}</b></>
                : 'Top level'}
            </span>
          </div>
        </div>
        {children && <div className={styles.explainer}>{children}</div>}
      </div>

      <ol className={styles.ladder} aria-label="Trust levels">
        {TRUST_LEVELS.map((l, i) => {
          const end = TRUST_LEVELS[i + 1]?.min ?? 101
          // How much of this level's band the score fills, 0 to 1.
          const fill = Math.max(0, Math.min(1, (score - l.min + 1) / (end - l.min)))
          const current = l.level === level
          return (
            <li key={l.level} className={current ? `${styles.step} ${styles.current}` : styles.step} aria-current={current ? 'step' : undefined}>
              <span className={styles.bar}><i className={styles.fill} style={{ width: `${fill * 100}%` }} /></span>
              <span className={styles.stepLabel}>{l.level} <span className={styles.stepMin}>{l.min}</span></span>
            </li>
          )
        })}
      </ol>
    </section>
  )
}
